import type { Placement } from '@popperjs/core';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type Dispatch,
  type RefObject,
  type SetStateAction,
} from 'react';
import { usePopper } from 'react-popper';
import { useTheme } from 'styled-components';
import { useOnClickOutside } from '../../../hooks/useOnClickOutside';

export interface UseDropdownPopperProps {
  isOpen: boolean;
  dropdownWidth?: CSSProperties['width'];
  dropdownHeight?: CSSProperties['height'];
  dropdownPlacement?: Placement;
  /** The referenceElement is the element which the dropdown is anchored to */
  referenceElement: HTMLElement | null;
  onClickOutside?: (e: MouseEvent | TouchEvent) => void;
  offset?: number;
  padding?: number;
  /** The dropdownContentRef will be attached to the container of the content within the dropdown */
  dropdownContentRef?: RefObject<HTMLDivElement>;
  /**
   * If set to false, will not react to size or position changes in the reference element.
   * The dropdown will just update as isOpen becomes true. Defaults to true.
   */
  reactToRefElementChanges?: boolean;
  /** An option on Popper.js's preventOverflow modifier. Defaults to true.
   *
   * When disabled, "disconnects" / untethers the popper from the anchor element. This tells Popper.js that it is okay
   * to position the popper in a "disconnected" manner from the anchor element if that is necessary to satisfy the
   * "preventOverflow" mandate.
   *
   * Makes sense to use when you have a large popper, and are at risk of having smaller area to render within.
   */
  tetherToRefElement?: boolean;
  /** If true, we will not wait for an Animation Frame before updating the position of the dropdown when the content size
   * within the dropdown changes. This can help with fluidity in some cases. Defaults to false (waiting for an animation frame).
   */
  immediateContentSizeUpdate?: boolean;
}

export interface UseDropdownPopperOutput {
  setPopperElement: Dispatch<SetStateAction<HTMLElement | null>>;
  popper: ReturnType<typeof usePopper>;
  dropdownWidth?: CSSProperties['width'];
  dropdownHeight?: CSSProperties['height'];
  /**
   * Reading layout properties during render out in the view can lead to bad performance.
   * This hook gets the referenceElement's width property ready for the consumer so we don't get into that bad situation.
   */
  referenceWidth?: number;
  referenceElement: HTMLElement | null;
  isOpen: boolean;
  dropdownContentRef: RefObject<HTMLDivElement>;
}

export const useDropdownPopper = ({
  isOpen,
  dropdownPlacement,
  dropdownWidth,
  dropdownHeight,
  referenceElement,
  onClickOutside,
  offset = 0,
  padding = 0,
  dropdownContentRef,
  reactToRefElementChanges = true,
  immediateContentSizeUpdate = false,
  tetherToRefElement = true,
}: UseDropdownPopperProps): UseDropdownPopperOutput => {
  const { spacing4 } = useTheme();
  const _offset = offset || spacing4;
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null);
  const [referenceWidth, setReferenceWidth] = useState<number>();
  // if we are applying a manual width, we want the dropdown to be just bottom. This will split the additional width evenly across left and right sides.
  // if we are going for auto-width, then set it to bottom-start to align dropdown with input field exactly.
  const popperPlacement = dropdownPlacement ? dropdownPlacement : dropdownWidth ? 'bottom' : 'bottom-start';
  const popper = usePopper(referenceElement, popperElement, {
    placement: popperPlacement,
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, _offset],
        },
      },
      {
        name: 'preventOverflow',
        options: {
          padding,
          tether: tetherToRefElement,
          altAxis: !tetherToRefElement, // Allows the popover to be shifted along the altAxis, possibly overlapping target element
        },
      },
      {
        name: 'eventListeners',
        options: {
          scroll: reactToRefElementChanges,
          resize: reactToRefElementChanges,
        },
      },
      {
        name: 'computeStyles',
        options: {
          adaptive: reactToRefElementChanges,
        },
      },
    ],
  });
  const { update } = popper;

  const defaultDropdownContentRef = useRef<HTMLDivElement>(null);
  const dropdownContentRefToUse = dropdownContentRef ?? defaultDropdownContentRef;
  const contentSizeUpdateFrame = useRef<number>();
  const contentSizeUpdateTimeout = useRef<number>();

  const handleClickOutside = useCallback(
    (e: MouseEvent | TouchEvent) => {
      onClickOutside && onClickOutside(e);
    },
    [onClickOutside]
  );

  useOnClickOutside(dropdownContentRefToUse, isOpen ? handleClickOutside : undefined);

  const setNextReferenceWidth = useCallback((nextReferenceWidth: number | undefined) => {
    setReferenceWidth(previousReferenceWidth =>
      previousReferenceWidth === nextReferenceWidth ? previousReferenceWidth : nextReferenceWidth
    );
  }, []);

  const updateReferenceWidth = useCallback(
    (entry: ResizeObserverEntry) => {
      const borderBoxSize = Array.isArray(entry.borderBoxSize) ? entry.borderBoxSize.at(0) : entry.borderBoxSize;
      setNextReferenceWidth(borderBoxSize?.inlineSize ?? entry.contentRect.width);
    },
    [setNextReferenceWidth]
  );

  useLayoutEffect(() => {
    if (!referenceElement) {
      setNextReferenceWidth(undefined);
      return;
    }

    // Grab the referenceElement's width out-of-render here to have the correct initial value.
    // Updates are provided via the resize observer below.
    setNextReferenceWidth(referenceElement.getBoundingClientRect().width);
  }, [referenceElement, setNextReferenceWidth]);

  // We need to manually listen to resizes in the referenceElement
  // and when that resizes, we update the popper to follow along.
  useEffect(() => {
    if (referenceElement) {
      const observer = new ResizeObserver(entries => {
        for (const entry of entries) {
          updateReferenceWidth(entry);
        }

        if (update && reactToRefElementChanges) {
          window.requestAnimationFrame(() => {
            // Guard against calling update() on a disconnected element — its getBoundingClientRect() returns
            // {0,0,0,0} which would send the dropdown to the upper-left corner of the viewport.
            if (referenceElement.isConnected) {
              update();
            }
          });
        }
      });
      observer.observe(referenceElement);
      return () => observer.disconnect();
    }
  }, [referenceElement, reactToRefElementChanges, update, updateReferenceWidth]);

  // Similarly to above, we also listen to size changed in the content of our dropdown.
  // When the height changes, we might have to update our position. Usually this is just done on init+1 after
  // the component realizes what height its content takes up.
  useEffect(() => {
    if (dropdownContentRefToUse.current && update) {
      const clearPendingContentSizeUpdate = () => {
        if (contentSizeUpdateFrame.current != null) {
          window.cancelAnimationFrame(contentSizeUpdateFrame.current);
          contentSizeUpdateFrame.current = undefined;
        }

        if (contentSizeUpdateTimeout.current != null) {
          window.clearTimeout(contentSizeUpdateTimeout.current);
          contentSizeUpdateTimeout.current = undefined;
        }
      };

      const scheduleContentSizeUpdate = () => {
        if (contentSizeUpdateFrame.current != null || contentSizeUpdateTimeout.current != null) {
          return;
        }

        if (immediateContentSizeUpdate) {
          // Leave the ResizeObserver delivery cycle before Popper reads/writes layout.
          contentSizeUpdateTimeout.current = window.setTimeout(() => {
            contentSizeUpdateTimeout.current = undefined;
            update();
          });
          return;
        }

        contentSizeUpdateFrame.current = window.requestAnimationFrame(() => {
          contentSizeUpdateFrame.current = undefined;
          update();
        });
      };

      const observer = new ResizeObserver(() => {
        scheduleContentSizeUpdate();
      });
      observer.observe(dropdownContentRefToUse.current);
      return () => {
        observer.disconnect();
        clearPendingContentSizeUpdate();
      };
    }
  }, [dropdownContentRefToUse, immediateContentSizeUpdate, update]);

  // We also need to call update every time the referenceElement changes
  // https://github.com/floating-ui/floating-ui/issues/538#issuecomment-596601887
  useEffect(() => {
    if (referenceElement?.isConnected) {
      update?.();
    }
  }, [referenceElement, update]);

  // Whenever we open the dropdown, lets update the position as well to make sure its opened in the right place
  useEffect(() => {
    if (isOpen && update) {
      update();
    }
  }, [isOpen, update]);

  return {
    setPopperElement,
    popper,
    dropdownWidth,
    dropdownHeight,
    referenceWidth,
    referenceElement,
    isOpen,
    dropdownContentRef: dropdownContentRefToUse,
  };
};
